import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { IconButton, useTheme } from '@mui/material';
import { orderBy as lodashOrderBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Order, Table } from 'web-components-ts';
import { COLOR_PRIMARY } from 'attrs/colors';

import { IUser } from 'types/interfaces/iUser';
import { getDialCodeFromCountryCode, getUserRole, isSuccess } from '../../helpers/utils';
import { getUsers } from '../../redux/rootSelectors';
import { getUserData } from '../../redux/user/selectors';
import { updateUserRole } from '../../redux/users/actions';
import Delete from './Delete';
import EditUserDialog from './EditUserDialog';
import { StyledContainer, TableContainer } from './elements';

interface UserTableProps {
  rows: IUser[];
  selectedTab: string;
}

const UserTable = ({ rows, selectedTab }: UserTableProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [orderBy, setOrderBy] = useState('first_name');
  const [order, setOrder] = useState('asc');
  const [isOpen, setIsOpen] = useState(false);
  const [editUser, setEditUser] = useState({});
  const { id: loggedInUserId } = useSelector(getUserData);
  const { updateLoadingState } = useSelector(getUsers);

  const closeModal = () => {
    setIsOpen(false);
  };

  const OpenModal = (user: IUser) => {
    setIsOpen(true);
    setEditUser(user);
  };

  const handleRoleSubmit = (values: IUser) => {
    dispatch(updateUserRole(values.id, values));
  };

  useEffect(() => {
    if (isSuccess(updateLoadingState.status)) {
      closeModal();
    }
  }, [updateLoadingState]);

  const roleColumn = {
    field: 'role',
    label: t('user_management.columns.role'),
    sort: false,
    render: (row: IUser) => <>{t(getUserRole(row.role).label)}</>
  };

  const emailColumn = {
    field: 'email',
    label: t('user_management.columns.email'),
    sort: false,
    render: (row: IUser) => <>{row['e-mail']}</>
  };

  const actionsColumn = {
    field: 'actions',
    label: '',
    sort: false,
    render: (row: IUser) => {
      const isLoggedInUser = row.id === loggedInUserId;
      return !isLoggedInUser ? (
        <div>
          <IconButton aria-label="edit_role" color="primary" onClick={() => OpenModal(row)}>
            <EditOutlinedIcon htmlColor={COLOR_PRIMARY} />
          </IconButton>
          <Delete user={row} />
        </div>
      ) : (
        <></>
      );
    }
  };

  const userTableColumns = [
    {
      field: 'first_name',
      label: t('user_management.columns.first_name'),
      sort: true,
      render: (row: IUser) => <>{row.is_active ? row.first_name : ''}</>
    },
    {
      field: 'last_name',
      label: t('user_management.columns.last_name'),
      sort: true,
      render: (row: IUser) => <>{row.is_active ? row.last_name : ''}</>
    },
    roleColumn,
    emailColumn,
    {
      field: 'phone',
      label: t('user_management.columns.phone'),
      sort: false,
      render: (row: IUser) => {
        const selectedDialCode = getDialCodeFromCountryCode(((row || {}).phone || {}).country);
        const formattedNumber = `${selectedDialCode} ${((row || {}).phone || {}).number}`;
        return <>{row.is_active ? formattedNumber : ''}</>;
      }
    },
    actionsColumn
  ];

  const pendingUserTableColumns = [roleColumn, emailColumn, actionsColumn];

  const columns = selectedTab === 'active' ? userTableColumns : pendingUserTableColumns;

  const tableData = lodashOrderBy(rows, item => item[orderBy as keyof IUser], order as Order);

  const sortTable = (orderParam: string, orderByParam: string) => {
    setOrder(orderParam);
    setOrderBy(orderByParam);
  };

  return (
    <>
      <StyledContainer maxWidth={false} theme={theme}>
        <TableContainer>
          <Table rowKey="id" hasFooter={false} columns={columns} dataSource={tableData} handleTableSort={sortTable} />
        </TableContainer>
      </StyledContainer>
      <EditUserDialog isOpen={isOpen} handleCLose={closeModal} handleFormSubmit={handleRoleSubmit} user={editUser} />
    </>
  );
};

export default UserTable;
